import { startOfMonth, addMonths, subDays, isAfter, startOfDay } from 'date-fns'

const DEFAULT_REPORTING_PERIOD_START_DATE = new Date('2024-01-01')
const DEFAULT_REPORTING_PERIOD_END_DATE = new Date('2027-12-31')

const getDateIncludedReportingPeriod = ({
  startMonth,
  monthInterval,
  date
}: {
  startMonth: number
  monthInterval: number
  date: Date
}): {
  startDate: Date
  endDate: Date
} => {
  const convertedStartMonth = startMonth - 1
  const year = date.getFullYear()
  const month = date.getMonth()

  if (monthInterval === 12) {
    const startDate =
      month < convertedStartMonth
        ? new Date(year - 1, convertedStartMonth, 1)
        : new Date(year, convertedStartMonth, 1)

    // Add 12 months to start date and subtract 1 day
    const endDate = new Date(startDate)
    endDate.setMonth(startDate.getMonth() + 12)
    endDate.setDate(0)
    endDate.setHours(23)
    endDate.setMinutes(59)
    endDate.setSeconds(59)

    return {
      startDate,
      endDate
    }
  }

  // Calculate which interval (0-based index)
  const intervalIndex = Math.floor(((month - convertedStartMonth + 12) % 12) / monthInterval)

  // Start month of interval
  const intervalStartMonth = (convertedStartMonth + intervalIndex * monthInterval + 12) % 12

  // End month of interval (exclusive)
  const intervalEndMonth = (intervalStartMonth + monthInterval) % 12

  const startYear = intervalStartMonth > month ? year - 1 : year
  const endYear = intervalEndMonth <= intervalStartMonth ? startYear + 1 : startYear

  return {
    startDate: new Date(startYear, intervalStartMonth),
    endDate: new Date(endYear, intervalEndMonth, 0, 23, 59, 59) // Last day of the end month
  }
}

export function getInstallationReportingPeriods(
  startMonth: number,
  monthInterval: number,
  startDate = DEFAULT_REPORTING_PERIOD_START_DATE,
  endDate = DEFAULT_REPORTING_PERIOD_END_DATE
): { startDate: Date; endDate: Date }[] {
  const { startDate: reportingPeriodFirstStartDate } = getDateIncludedReportingPeriod({
    monthInterval,
    startMonth,
    date: startDate
  })

  // Get the first day of the next period
  const nextPeriodStart = startOfMonth(addMonths(reportingPeriodFirstStartDate, monthInterval))
  // Then subtract one day and get start of that day for the end date
  const firstPeriodEnd = startOfDay(subDays(nextPeriodStart, 1))

  const periods: { startDate: Date; endDate: Date }[] = [
    {
      startDate: startOfDay(startOfMonth(reportingPeriodFirstStartDate)),
      endDate: firstPeriodEnd
    }
  ]

  // Start the next period
  let currentStartDate = startOfDay(startOfMonth(nextPeriodStart))

  while (isAfter(endDate, currentStartDate)) {
    const nextPeriodStart = startOfMonth(addMonths(currentStartDate, monthInterval))
    const periodEnd = startOfDay(subDays(nextPeriodStart, 1))
    const actualEndDate = isAfter(periodEnd, endDate) ? endDate : periodEnd

    periods.push({
      startDate: currentStartDate,
      endDate: actualEndDate
    })

    // Set up next period
    currentStartDate = startOfDay(startOfMonth(nextPeriodStart))
  }

  return periods
}
